import React, {useEffect, useState} from 'react';
import {css} from 'emotion';
import Text from '@amzn/meridian/text';
import {useHistory, useParams} from 'react-router-dom';
import {useAppSelector} from '../../app/hooks';
import {selectCachedBusiness, selectCachedCountry, selectCachedRegion} from '../cached-state/cachedStateSlice';
import SiteDetailsTable from './components/SiteDetailsTable';
import SiteWindowConstraintTable from './components/SiteWindowConstraintTable';
import SitePITConstraintTable from './components/SitePITConstraintTable';

import {GetForecastResponse, Granularity} from '../../common/apis/models/getForecastResponse';
import {GetSiteResponse} from '../../common/apis/models/getSiteResponse';
import {getForecast, getConstraintsList, getSite} from '../../common/apis/BackendApisClient';
import {GetConstraintsListResponse} from '../../common/apis/models/getConstraintsListResponse';
import buildSiteMetadata from './utils/buildSiteMetadata';
import {IPageProps} from '../../common/pages/models';
import BucklePage from '../../common/pages/BucklePage';
import PageLoading from '../../common/components/PageLoading';
import analyzeConstraints from './utils/analyzeConstraints';
import {getLocalizedAPIsStartDate} from '../../common/utils/dates';

const mastheadAndColumnSpacing = css`
  padding-top: 18px;
  padding-left: 1.5%;
  padding-right: 1.5%;
`;

const otherConstraints = css`
  padding-top: 18px;
`;

const granularitySectionStyle = css`
  padding-top: 12px;
`;

const granularityLabelStyle = css`
  color: #555;
  padding-bottom: 4px;
`;

const siteHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: 100px;
  padding: 14px 0 14px 48px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const siteIdStyle = css`
  font-size: 28px;
  font-weight: bold;
  min-width: 80px;
  text-transform: uppercase;
`;

const metadataGridStyle = css`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 6px 40px;
  align-items: center;
  flex: 1;
`;

const metadataLabelStyle = css`
  font-size: 13px;
  color: #666;
  font-weight: 600;
`;

const metadataValueStyle = css`
  font-size: 13px;
`;

interface IUrlParams {
  site: string;
}

const SiteDetailsPage = ({setEntityMetadata, setPageTitle}: IPageProps) => {
  const history = useHistory();
  const {site} = useParams<IUrlParams>();
  const businessSelection = useAppSelector(selectCachedBusiness);
  const regionSelection = useAppSelector(selectCachedRegion);
  const countrySelection = useAppSelector(selectCachedCountry);

  const [siteDetails, setSiteDetails] = useState<GetSiteResponse | void>();
  const [forecast, setForecast] = useState<GetForecastResponse | void>();
  const [constraintsList, setConstraintsList] = useState<GetConstraintsListResponse | void>();

  useEffect(() => {
    setPageTitle(site);
  }, [setPageTitle, site]);

  const currDate = new Date();
  const startTime = getLocalizedAPIsStartDate(currDate);

  useEffect(() => {
    getSite({
      businessType: businessSelection!,
      region: regionSelection!,
      siteId: site,
      startTime: startTime,
    }).then((response) => {
      if (!response) {
        history.push('/404');
      }

      setSiteDetails(response);
      if (response) {
        setPageTitle(response.site.siteId || '');
        setEntityMetadata(buildSiteMetadata(response));
      }
    });
  }, [businessSelection, regionSelection, site, startTime, history, setSiteDetails, setPageTitle, setEntityMetadata]);

  useEffect(() => {
    getForecast({
      businessType: businessSelection!,
      region: regionSelection!,
      siteId: site,
      granularity: Granularity.Week,
      startTime: startTime,
    }).then((response) => {
      setForecast(response);
    });
  }, [businessSelection, regionSelection, site, startTime, setForecast]);

  useEffect(() => {
    getConstraintsList({
      businessType: businessSelection!,
      region: regionSelection!,
      query: '',
    }).then((response) => {
      setConstraintsList(response);
    });
  }, [businessSelection, regionSelection]);

  if (siteDetails && forecast && constraintsList) {
    // Build a map of constraintId -> inputGranularity
    const granularityMap: Record<string, string> = {};
    constraintsList.records.forEach((record) => {
      granularityMap[record.constraintId] = record.inputGranularity;
    });

    const {constraintsAffectingOperationsData, otherConstraintsData, minimumConstraints, summaryRecords} = analyzeConstraints(
      siteDetails,
      forecast
    );

    // Group constraints by granularity
    const weeklyConstraints = constraintsAffectingOperationsData.filter(
      (c) => granularityMap[c.constraintId] === 'WEEKLY' || !granularityMap[c.constraintId]
    );
    const windowConstraints = constraintsAffectingOperationsData.filter(
      (c) => granularityMap[c.constraintId] === 'WINDOW'
    );
    const pitConstraints = constraintsAffectingOperationsData.filter(
      (c) => granularityMap[c.constraintId] === 'PIT'
    );
    const dailyConstraints = constraintsAffectingOperationsData.filter(
      (c) => granularityMap[c.constraintId] === 'DAILY'
    );

    // Group "other" constraints by granularity too
    const otherWeekly = otherConstraintsData.filter(
      (c) => granularityMap[c.constraintId] === 'WEEKLY' || !granularityMap[c.constraintId]
    );
    const otherWindow = otherConstraintsData.filter(
      (c) => granularityMap[c.constraintId] === 'WINDOW'
    );
    const otherPit = otherConstraintsData.filter(
      (c) => granularityMap[c.constraintId] === 'PIT'
    );
    const otherDaily = otherConstraintsData.filter(
      (c) => granularityMap[c.constraintId] === 'DAILY'
    );

    const siteMetadata = buildSiteMetadata(siteDetails);

    return (
      <div className={mastheadAndColumnSpacing}>
        <div className={siteHeaderStyle}>
          <div className={siteIdStyle}>
            {siteDetails.site.siteId}
          </div>
          <div className={metadataGridStyle}>
            {siteMetadata.map((item) => (
              <React.Fragment key={item.label}>
                <div className={metadataLabelStyle}>{item.label}</div>
                <div className={metadataValueStyle}>{item.value || '—'}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <Text type="h300">Constraints Impacting Operational Capacity</Text>
        <label style={{fontStyle: 'italic', fontSize: 15}}>
          (Constraints input as ORDERS are converted to UNITS to be shown here)
        </label>

        {weeklyConstraints.length > 0 && (
          <div className={granularitySectionStyle}>
            <Text type="h100" className={granularityLabelStyle}>Weekly Constraints</Text>
            <SiteDetailsTable
              history={history}
              allowSorting={false}
              allowPaging={false}
              allowFiltering={false}
              constraintData={weeklyConstraints}
              highlightedDates={minimumConstraints}
              summaryRecords={summaryRecords}
            />
          </div>
        )}

        {dailyConstraints.length > 0 && (
          <div className={granularitySectionStyle}>
            <Text type="h100" className={granularityLabelStyle}>Daily Constraints</Text>
            <SiteDetailsTable
              history={history}
              allowSorting={false}
              allowPaging={false}
              allowFiltering={false}
              constraintData={dailyConstraints}
              highlightedDates={minimumConstraints}
              summaryRecords={[]}
            />
          </div>
        )}

        {windowConstraints.length > 0 && (
          <div className={granularitySectionStyle}>
            <Text type="h100" className={granularityLabelStyle}>Window Constraints</Text>
            <SiteWindowConstraintTable
              history={history}
              windowConstraints={windowConstraints}
              siteId={site}
              businessType={businessSelection!}
              region={regionSelection!}
              country={countrySelection ?? undefined}
            />
          </div>
        )}

        {pitConstraints.length > 0 && (
          <div className={granularitySectionStyle}>
            <Text type="h100" className={granularityLabelStyle}>Point-in-Time Constraints</Text>
            <SitePITConstraintTable
              history={history}
              pitConstraints={pitConstraints}
              highlightedDates={minimumConstraints}
              siteId={site}
            />
          </div>
        )}

        {/* Show ungrouped if no granularity data and there are constraints */}
        {weeklyConstraints.length === 0 && dailyConstraints.length === 0 &&
         windowConstraints.length === 0 && pitConstraints.length === 0 &&
         constraintsAffectingOperationsData.length > 0 && (
          <SiteDetailsTable
            history={history}
            allowSorting={false}
            allowPaging={false}
            allowFiltering={false}
            constraintData={constraintsAffectingOperationsData}
            highlightedDates={minimumConstraints}
            summaryRecords={summaryRecords}
          />
        )}

        {otherConstraintsData.length > 0 && (
          <div className={otherConstraints}>
            <Text type="h300">Other Constraints</Text>

            {otherWeekly.length > 0 && (
              <div className={granularitySectionStyle}>
                <Text type="h100" className={granularityLabelStyle}>Weekly</Text>
                <SiteDetailsTable
                  history={history}
                  allowSorting={true}
                  allowPaging={true}
                  allowFiltering={false}
                  constraintData={otherWeekly}
                  highlightedDates={{}}
                  summaryRecords={[]}
                />
              </div>
            )}

            {otherDaily.length > 0 && (
              <div className={granularitySectionStyle}>
                <Text type="h100" className={granularityLabelStyle}>Daily</Text>
                <SiteDetailsTable
                  history={history}
                  allowSorting={true}
                  allowPaging={true}
                  allowFiltering={false}
                  constraintData={otherDaily}
                  highlightedDates={{}}
                  summaryRecords={[]}
                />
              </div>
            )}

            {otherWindow.length > 0 && (
              <div className={granularitySectionStyle}>
                <Text type="h100" className={granularityLabelStyle}>Window</Text>
                <SiteWindowConstraintTable
                  history={history}
                  windowConstraints={otherWindow}
                  siteId={site}
                  businessType={businessSelection!}
                  region={regionSelection!}
                  country={countrySelection ?? undefined}
                />
              </div>
            )}

            {otherPit.length > 0 && (
              <div className={granularitySectionStyle}>
                <Text type="h100" className={granularityLabelStyle}>Point-in-Time</Text>
                <SitePITConstraintTable
                  history={history}
                  pitConstraints={otherPit}
                  highlightedDates={{}}
                  siteId={site}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  } else {
    return <PageLoading />;
  }
};

export default (props: IPageProps) => (
  <BucklePage {...props}>
    <SiteDetailsPage {...props} />
  </BucklePage>
);
